import log from "loglevel"
import { useEffect } from "react"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"
import { useNexusNavigate } from "../hooks/useNexusNavigate"
import { useQuery } from "../hooks/useQuery"
import { FlightPlanFlow } from "../models/FlightPlanFlow"
import { routes } from "../routing/NexRoutes"
import { ToolbarOptions } from "../store/types/AuthTypes"
import { useAuthStore } from "../zstore/AuthStore"
import { useDbStateStore } from "../zstore/DbStateStore"

/**
 * Check if we need to redirect the user or refresh auth info
 */
export const AuthComponent = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const nexusNavigate = useNexusNavigate()
    const query = useQuery()

    const userMgmtUrl = useDbStateStore((state) => state.userMgmtUrl)
    const login = useAuthStore((state) => state.login)
    const insiteToken = useAuthStore((state) => state.insiteToken)
    const setInsiteToken = useAuthStore((state) => state.setInsiteToken)
    const user = useAuthStore((state) => state.user)
    const permissions = useAuthStore((state) => state.permissions)
    const checkToolbarAccess = useAuthStore((state) => state.checkToolbarAccess)
    const checkTokenExpiration = useAuthStore((state) => state.checkTokenExpiration)
    const loginError = useAuthStore((state) => state.loginError)

    // Insite token from URL
    const insiteTokenParamName = "token"
    const queryToken = query.get(insiteTokenParamName)

    useEffect(() => {
        if (queryToken != null) {
            log.info("Found query token, so set auth token in local storage and redirect to home.")
            setInsiteToken(queryToken)

            const searchParams = new URLSearchParams(location.search)
            searchParams.delete(insiteTokenParamName)
            navigate({ ...location, search: searchParams.toString() }, { replace: true })
        }
    }, [queryToken])

    /**
     * If we have an insite token, and no user present, then check token expiration and login.
     */
    useEffect(() => {
        if (insiteToken != null && user == null) {
            const perform = async () => {
                if (insiteToken) {
                    // Automatically logs user out if token is expired
                    const fresh = await checkTokenExpiration()
                    if (fresh) {
                        log.info("Auth token is valid, proceed to refresh user auth.")
                        await login()
                    }
                }
            }
            perform()
        }
    }, [insiteToken, user == null])

    /**
     * Redirect to UserMgmt if we have a userMgmtUrl, no query token, no saved token, and no login error. Or if the
     * token is expired.
     */
    useEffect(() => {
        if (userMgmtUrl != null && queryToken == null && insiteToken == null && loginError == null) {
            log.info(`Go to user management url ${userMgmtUrl}.`)
            nexusNavigate.goToUserLogin()
        }
    }, [userMgmtUrl, queryToken, insiteToken, loginError])

    /**
     * Redirect to landing if user doesn't have permission for current
     * route.
     */
    useEffect(() => {
        if (permissions != null) {
            const pathname = location.pathname

            for (const [route, permissions] of Object.entries(routePermissions)) {
                if (pathname.includes(route) && !checkToolbarAccess(permissions)) {
                    nexusNavigate.goHome()
                    break
                }
            }
        }
    }, [location.pathname, permissions])

    return null
}

const routePermissions = {
    // app
    [routes.projectInit]: ToolbarOptions.Initialize_Project,
    // flightplans
    [routes.flightplanUpload]: ToolbarOptions.Submit_Flightplans,
    [`${routes.flightplanEditor}/${FlightPlanFlow.SUBMIT}`]: ToolbarOptions.Submit_Flightplans,
    [`${routes.flightplanEditor}/${FlightPlanFlow.EDIT}`]: ToolbarOptions.Edit_Flightplans,
    // missions
    [routes.missionUpload]: ToolbarOptions.Submit_Mission_Logs,
    [routes.missionSelect]: ToolbarOptions.Edit_Mission_Logs,
    [routes.missionReview]: ToolbarOptions.Flightline_QC_Review,
    // lookup
    [routes.lookupEditor]: ToolbarOptions.Edit_Lookup_Tables,
    // data receipts
    [routes.dataReceipts]: ToolbarOptions.Flightline_QC_Review,
    [routes.createDataReceipt]: ToolbarOptions.Flightline_QC_Review,
    // sensor installs
    [routes.sensorInstallations]: [ToolbarOptions.Edit_Mission_Logs, ToolbarOptions.Mission_Admin],
}
